import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { API, EASE, WELCOME_VIDEO } from "@/lib/site";

let welcomeVideoPromise = null;

function useWelcomeVideo() {
  const [video, setVideo] = useState(null);
  useEffect(() => {
    welcomeVideoPromise =
      welcomeVideoPromise ||
      axios
        .get(`${API}/videos/welcome`)
        .then((r) => (r.data && r.data.video_url ? r.data : WELCOME_VIDEO))
        .catch(() => WELCOME_VIDEO);
    welcomeVideoPromise.then(setVideo);
  }, []);
  return video;
}

export function WelcomeBgVideo() {
  const video = useWelcomeVideo();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 1500);
    return () => clearTimeout(t);
  }, []);
  if (!ready || !video || !video.bg_url) return null;
  return (
    <video
      data-testid="welcome-bg-video"
      className="absolute inset-0 w-full h-[120%] object-cover opacity-40"
      poster={video.poster_url || undefined}
      autoPlay
      muted
      loop
      playsInline
      aria-hidden="true"
    >
      <source src={video.bg_url} type={video.bg_url.endsWith(".webm") ? 'video/webm; codecs="vp8"' : undefined} />
    </video>
  );
}

export function VideoModal({ video, open, setOpen }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="welcome-video-modal"
        className="max-w-md border-gold/30 bg-inkdeep p-2 sm:p-3"
      >
        <DialogTitle className="sr-only">{video.title || "Welcome from Beth El"} — video</DialogTitle>
        <div className="flex justify-center">
          {open && (
            <video
              data-testid="welcome-video-player"
              src={video.video_url}
              poster={video.poster_url || undefined}
              className="max-h-[78vh] w-auto max-w-full"
              controls
              autoPlay
              playsInline
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WelcomeVideoCard() {
  const video = useWelcomeVideo();
  const [open, setOpen] = useState(false);
  if (!video || !video.video_url) return null;
  return (
    <>
      <motion.button
        type="button"
        data-testid="welcome-video-open-button"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.3, ease: EASE }}
        whileHover={{ y: -4 }}
        className="group text-left border border-gold/40 bg-inkdeep/60 backdrop-blur-md p-3 flex items-center gap-4 hover:border-gold transition-colors duration-400"
      >
        <span className="relative block w-16 shrink-0 aspect-[3/4] overflow-hidden">
          {video.poster_url && <img src={video.poster_url} alt="" className="w-full h-full object-cover object-top" />}
          <span className="absolute inset-0 bg-inkdeep/40 group-hover:bg-inkdeep/20 transition-colors duration-400" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-9 h-9 rounded-full bg-gold text-inkdeep flex items-center justify-center group-hover:scale-110 transition-transform duration-400">
              <Play size={15} className="ml-0.5" fill="currentColor" />
            </span>
          </span>
        </span>
        <span>
          <span className="block text-[10px] tracking-[0.3em] uppercase text-gold">
            Welcome from Beth El
          </span>
          <span className="block mt-1 font-cormorant italic text-foreground/80 text-base leading-snug">
            Watch our message for you — with sound
          </span>
        </span>
      </motion.button>
      <VideoModal video={video} open={open} setOpen={setOpen} />
    </>
  );
}

export function WelcomeVideoFeature() {
  const video = useWelcomeVideo();
  const [open, setOpen] = useState(false);
  if (!video || !video.video_url) return null;
  return (
    <>
      <button
        type="button"
        data-testid="story-video-open-button"
        onClick={() => setOpen(true)}
        className="group w-full text-left border border-gold/60 bg-white/60 p-5 md:p-6 flex items-center gap-5 md:gap-8 hover:border-[#8F7233] transition-colors duration-400"
      >
        <span className="relative block w-20 md:w-24 shrink-0 aspect-[3/4] overflow-hidden">
          {video.poster_url && <img src={video.poster_url} alt="" className="w-full h-full object-cover object-top" loading="lazy" />}
          <span className="absolute inset-0 bg-inkdeep/25 group-hover:bg-inkdeep/10 transition-colors duration-400" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-11 h-11 rounded-full bg-gold text-inkdeep flex items-center justify-center group-hover:scale-110 transition-transform duration-400">
              <Play size={17} className="ml-0.5" fill="currentColor" />
            </span>
          </span>
        </span>
        <span className="flex-1">
          <span className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-2">
            A Welcome in Person
          </span>
          <span className="block font-syne font-bold uppercase tracking-tight text-lg md:text-2xl leading-tight">
            {video.title || "Welcome from Beth El"}
          </span>
          <span className="block mt-2 font-cormorant italic text-foreground/60 text-base md:text-lg">
            Press play — sound on
          </span>
        </span>
      </button>
      <VideoModal video={video} open={open} setOpen={setOpen} />
    </>
  );
}
